import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { logAuthEvent } from '../../lib/authLogger';
import AuthLayout from './AuthLayout';
import { useSession } from './useSession';
import { validateEmail, sanitizeHTML } from '../../lib/validation';
import Turnstile from './Turnstile';

const Login = () => {
  const navigate = useNavigate();
  const { session, loading: sessionLoading } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (session && !sessionLoading) {
      navigate('/file-naming', { replace: true });
    }
  }, [session, sessionLoading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validate Turnstile token
    if (!turnstileToken) {
      setError('Please complete the security verification');
      setLoading(false);
      return;
    }

    // Validate and sanitize email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      setLoading(false);
      return;
    }

    const sanitizedEmail = emailValidation.sanitized;

    // Basic password validation (non-empty)
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
        options: {
          captchaToken: turnstileToken
        }
      });

      if (authError) {
        const sanitizedError = sanitizeHTML(authError.message);
        setError(sanitizedError);
        logAuthEvent({
          type: 'login',
          email: sanitizedEmail,
          success: false,
          message: sanitizedError,
          meta: { code: authError.code }
        });
        return;
      }

      logAuthEvent({ type: 'login', email: sanitizedEmail, success: true, message: 'Login successful' });

      // Let the useEffect handle navigation when session updates

    } catch (err) {
      const msg = sanitizeHTML(err?.message || 'Unexpected error while logging in');
      setError(msg);
      logAuthEvent({ type: 'login', email: sanitizedEmail, success: false, message: msg });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking existing session
  if (sessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-gray-600 font-medium animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <AuthLayout title="Sign In" subtitle="Access your account">
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <div className="text-right mt-2">
            <a
              href="https://tracker.patoexpectsspeed.com/forgot-password"
              className="text-sm text-blue-600 hover:underline"
            >
              Forgot password?
            </a>
          </div>
        </div>
        <Turnstile
          onVerify={(token) => setTurnstileToken(token)}
          onError={() => {
            setTurnstileToken(null);
            setError('Security verification failed. Please try again.');
          }}
          onExpire={() => {
            setTurnstileToken(null);
            setError('Security verification expired. Please verify again.');
          }}
        />
  {error && <div role="alert" aria-live="polite" className="text-sm text-red-600 font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        <button
          type="submit"
          disabled={loading || !turnstileToken}
          className="w-full inline-flex justify-center items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow hover:from-blue-700 hover:to-blue-800 disabled:opacity-60"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Login;